import { Box, CircularProgress, Icon, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useStrategy } from "../api/strategy";
import { useAssetTilesUrl, useAssetCounts } from "../api/assets";
import { useMap } from "../contexts/MapContext";

const barSx = {
  position: "fixed",
  bottom: 0,
  left: 0,
  width: "100%",
  height: 24,
  bgcolor: "#212b37",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  px: 2,
  zIndex: 2000,
} as const;

/** The bar along the bottom of the project page. What the pointer is over on
 *  the map always shows (the map's own bar sits under this one); otherwise the
 *  strategy status, which goes away two seconds after a success. */
export default function FooterStatusBar() {
  const { projectId } = useParams<{ projectId: string }>();
  const { status, isLoading } = useStrategy();
  const { data: tilesData } = useAssetTilesUrl(projectId, false);
  const { data: assetCounts } = useAssetCounts(projectId);
  const { hoverText } = useMap();
  const [visible, setVisible] = useState(true);

  const tilesUrl = (tilesData as any)?.data?.tiles_url as string | undefined | null;
  const totalAssets = (assetCounts as any)?.data?.total ?? 0;
  const assetsLoading = !tilesUrl && totalAssets > 0;

  // When API loading begins or assets are loading, make bar visible
  useEffect(() => {
    if (isLoading || assetsLoading) {
      setVisible(true);
    }
  }, [isLoading, assetsLoading]);

  useEffect(() => {
    if (!status) return;
    const { state } = status as any;
    if (state === "succeeded") {
      setVisible(true);
      const timer = setTimeout(() => setVisible(false), 2000);
      return () => clearTimeout(timer);
    }
    setVisible(true);
  }, [status]);

  if (hoverText) {
    return (
      <Box sx={barSx}>
        <Typography variant="caption" sx={{ color: "#fff" }}>
          {hoverText}
        </Typography>
      </Box>
    );
  }

  if (!visible) return null;

  if (assetsLoading) {
    return (
      <Box sx={barSx}>
        <CircularProgress size={12} sx={{ color: "#fff", mr: 1 }} />
        <Typography variant="caption" sx={{ color: "#fff" }}>
          Loading assets...
        </Typography>
      </Box>
    );
  }

  if (!status && isLoading) {
    return (
      <Box sx={barSx}>
        <CircularProgress size={14} sx={{ color: "#fff", ml: 0.5 }} />
      </Box>
    );
  }

  if (!status) return null;

  const { message, state } = status as any;

  return (
    <Box sx={barSx}>
      {state === "running" || state === "queued" ? (
        <CircularProgress size={12} sx={{ color: "#fff", mr: 1 }} />
      ) : (
        <Icon sx={{ fontSize: 14, color: "#fff", mr: 1 }}>
          {state === "succeeded" ? "check_circle" : "error"}
        </Icon>
      )}
      <Typography variant="caption" sx={{ color: "#fff" }}>
        {message}
      </Typography>
    </Box>
  );
}
